import { useCallback, useEffect, useRef } from 'react';

export const usePolling = (callback: () => Promise<void>, interval = 5000) => {
  const intervalRef = useRef<number | undefined>(undefined);
  const callbackRef = useRef(callback);
  const periodRef = useRef(interval);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== undefined) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }, []);

  const arm = useCallback(() => {
    intervalRef.current = window.setInterval(() => {
      void callbackRef.current();
    }, periodRef.current);
  }, []);

  const startPolling = useCallback(
    (override?: () => Promise<void>) => {
      if (override) {
        callbackRef.current = override;
      }

      if (intervalRef.current !== undefined) {
        return;
      }

      arm();
    },
    [arm]
  );

  useEffect(() => {
    periodRef.current = interval;

    if (intervalRef.current === undefined) {
      return;
    }

    window.clearInterval(intervalRef.current);
    arm();
  }, [interval, arm]);

  useEffect(() => stopPolling, [stopPolling]);

  return { startPolling, stopPolling };
};
